import React, { useState } from 'react';
import {
  Settings,
  X,
  Save,
  Building2,
  FileText,
  Shield,
  Sparkles,
  CheckCircle2
} from 'lucide-react';
import { PracticeSettings } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';
import { getPracticeSettings, savePracticeSettings } from '../../lib/practiceManagementService';

interface PracticeSettingsModalProps {
  lang: Language;
  onClose: () => void;
  onSaved: () => void;
}

export const PracticeSettingsModal: React.FC<PracticeSettingsModalProps> = ({
  lang,
  onClose,
  onSaved
}) => {
  const isHi = lang === 'hi';
  const initial = getPracticeSettings();
  const [settings, setSettings] = useState<PracticeSettings>(initial);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSave = () => {
    savePracticeSettings(settings);
    setIsSuccess(true);
    setTimeout(() => {
      onSaved();
      onClose();
    }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-astro-primary/60 backdrop-blur-xs">
      <div className="bg-white dark:bg-slate-850 w-full max-w-xl rounded-2xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 space-y-4">
        {/* Modal Header */}
        <div className="p-4 bg-astro-secondary-surface dark:bg-astro-primary border-b border-astro-border dark:border-astro-strong-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-500" />
            <h3 className="text-sm font-black text-astro-text dark:text-astro-muted">
              {isHi ? 'ज्योतिष संस्थान एवं अभ्यास सेटिंग्स' : 'Practice Branding & Report Settings'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-astro-muted hover:text-astro-secondary-text dark:hover:text-astro-muted cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto text-xs">
          <div className="space-y-1">
            <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
              Practice / Institute Name:
            </label>
            <input
              type="text"
              value={settings.practiceName}
              onChange={(e) => setSettings({ ...settings, practiceName: e.target.value })}
              className="w-full p-2.5 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted font-bold"
            />
          </div>

          <div className="space-y-1">
            <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
              Tagline / Subtitle:
            </label>
            <input
              type="text"
              value={settings.tagline}
              onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
              className="w-full p-2.5 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
                Tradition / Shastra Lineage:
              </label>
              <input
                type="text"
                value={settings.traditionLineage}
                onChange={(e) => setSettings({ ...settings, traditionLineage: e.target.value })}
                className="w-full p-2.5 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
              />
            </div>
            <div className="space-y-1">
              <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
                Practice Location:
              </label>
              <input
                type="text"
                value={settings.location}
                onChange={(e) => setSettings({ ...settings, location: e.target.value })}
                className="w-full p-2.5 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
              Practitioner Signature & Seal Text:
            </label>
            <input
              type="text"
              value={settings.signatureText}
              onChange={(e) => setSettings({ ...settings, signatureText: e.target.value })}
              className="w-full p-2.5 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
            />
          </div>

          <div className="space-y-1">
            <label className="font-bold text-astro-secondary-text dark:text-astro-muted">
              Official Disclaimer in Reports:
            </label>
            <textarea
              rows={3}
              value={settings.officialDisclaimerText}
              onChange={(e) => setSettings({ ...settings, officialDisclaimerText: e.target.value })}
              className="w-full p-2.5 bg-astro-secondary-surface dark:bg-astro-primary rounded-lg border border-astro-border dark:border-astro-strong-border text-astro-text dark:text-astro-muted"
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-astro-secondary-surface dark:bg-astro-primary border-t border-astro-border dark:border-astro-strong-border flex items-center justify-end gap-2">
          {isSuccess && (
            <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" />
              <span>Saved</span>
            </span>
          )}
          <button
            onClick={onClose}
            className="px-3.5 py-1.5 rounded-lg bg-astro-card dark:bg-astro-secondary text-astro-secondary-text dark:text-astro-muted font-bold text-xs cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs flex items-center gap-1.5 shadow-xs cursor-pointer"
          >
            <Save className="w-3.5 h-3.5" />
            <span>Save Settings</span>
          </button>
        </div>
      </div>
    </div>
  );
};
